import { apiService, ApiResponse, ApiError } from './api';
import { API_ENDPOINTS } from '../config/api';

// Types pour les clients dans les enquêtes
export interface EnqueteClient {
  id: string;
  nom: string;
  pays: string;
  email: string;
  notes: string;
  siren?: string;
  siret?: string;
  ville: string;
  adresse: string;
  is_active: boolean;
  telephone: string;
  created_at: string;
  created_by: string;
  updated_at: string;
  code_postal: string;
  type_client: string;
}

// Types pour les enquêtes
export interface Enquete {
  id: string;
  titre: string;
  description: string;
  objectifs: string; // Objectifs de l'enquête / demande client
  elementsFournis: string; // Éléments fournis par le client
  statut: 'en_cours' | 'terminee' | 'en_attente' | 'archivee';
  priorite: 'haute' | 'moyenne' | 'basse';
  dateCreation: string;
  dateModification: string;
  client: string | EnqueteClient; // Peut être un ID string ou un objet EnqueteClient complet
  budget: number;
  assigne: string;
  tags: string[];
  notes: string[];
  documents: string[];
  
  // Informations de partage
  access_type?: 'owner' | 'shared' | 'none';
  shared_by_name?: string;
  shared_at?: string;
  share_reason?: string;
  is_shared_with_admin?: boolean;
}

export interface EnqueteFormData {
  titre: string;
  description: string;
  objectifs: string;
  elementsFournis: string;
  statut: Enquete['statut'];
  priorite: Enquete['priorite'];
  client: string;
  budget: number;
  assigne: string;
  tags: string[];
}

// Types pour les réponses API
export interface EnquetesResponse {
  enquetes: Enquete[];
  total: number;
  page: number;
  limit: number;
}

export interface EnqueteResponse {
  enquete: Enquete;
}

// Service pour la gestion des enquêtes
class EnqueteService {
  private static instance: EnqueteService;
  private localData: Enquete[] = [];
  private useLocalData: boolean = false; // Flag pour utiliser les données locales - DESACTIVE pour utiliser l'API

  private constructor() {
    this.initializeLocalData();
  }

  static getInstance(): EnqueteService {
    if (!EnqueteService.instance) {
      EnqueteService.instance = new EnqueteService();
    }
    return EnqueteService.instance;
  }

  // Initialisation des données locales
  private initializeLocalData(): void {
    this.localData = [
      {
        id: '1',
        titre: 'Enquête sur Jean Dupont',
        description: 'Recherche d\'informations sur les activités en ligne et la présence digitale',
        objectifs: 'Déterminer la présence de Jean Dupont sur les réseaux sociaux et ses activités en ligne.',
        elementsFournis: 'Accès aux réseaux sociaux de Jean Dupont, informations sur ses activités.',
        statut: 'en_cours',
        priorite: 'haute',
        dateCreation: '2024-01-15',
        dateModification: '2024-01-20',
        client: 'Cabinet Martin',
        budget: 5000,
        assigne: 'Détective Privé',
        tags: ['surveillance', 'digital', 'réseaux sociaux'],
        notes: ['Premier contact établi', 'Analyse des réseaux sociaux en cours'],
        documents: ['rapport_initial.pdf', 'photos_surveillance.zip']
      },
      {
        id: '2',
        titre: 'Surveillance Marie Martin',
        description: 'Analyse des réseaux sociaux et présence digitale',
        objectifs: 'Analyser la présence de Marie Martin sur les réseaux sociaux et sa présence digitale.',
        elementsFournis: 'Accès aux réseaux sociaux de Marie Martin, informations sur sa présence.',
        statut: 'terminee',
        priorite: 'moyenne',
        dateCreation: '2024-01-10',
        dateModification: '2024-01-18',
        client: 'Entreprise XYZ',
        budget: 3000,
        assigne: 'Détective Privé',
        tags: ['surveillance', 'réseaux sociaux'],
        notes: ['Enquête terminée avec succès', 'Rapport final remis'],
        documents: ['rapport_final.pdf', 'evidence.zip']
      },
      {
        id: '3',
        titre: 'Investigation Entreprise ABC',
        description: 'Audit de sécurité et recherche d\'informations',
        objectifs: 'Effectuer un audit de sécurité et rechercher des informations sur l\'entreprise ABC.',
        elementsFournis: 'Accès aux systèmes de l\'entreprise ABC, informations sur ses activités.',
        statut: 'en_attente',
        priorite: 'basse',
        dateCreation: '2024-01-20',
        dateModification: '2024-01-20',
        client: 'Cabinet Legal',
        budget: 8000,
        assigne: 'Détective Privé',
        tags: ['audit', 'sécurité', 'entreprise'],
        notes: ['En attente de validation client'],
        documents: []
      }
    ];
  }

  // Méthode pour basculer entre données locales et API
  setUseLocalData(useLocal: boolean): void {
    this.useLocalData = useLocal;
  }

  // Récupération de toutes les enquêtes
  async getEnquetes(params?: {
    page?: number;
    limit?: number;
    search?: string;
    statut?: string;
    priorite?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<EnquetesResponse>> {
    if (this.useLocalData) {
      return this.getEnquetesLocal(params);
    }

    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.search) queryParams.append('search', params.search);
      if (params?.statut) queryParams.append('statut', params.statut);
      if (params?.priorite) queryParams.append('priorite', params.priorite);
      if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);

      const endpoint = `/enquetes?${queryParams.toString()}`;
      return await apiService.get<EnquetesResponse>(endpoint);
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des enquêtes:', error);
      // Fallback vers les données locales en cas d'erreur API
      return this.getEnquetesLocal(params);
    }
  }

  // Version locale de getEnquetes
  private async getEnquetesLocal(params?: {
    page?: number;
    limit?: number;
    search?: string;
    statut?: string;
    priorite?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<EnquetesResponse>> {
    let filteredData = [...this.localData];

    // Filtrage par recherche
    if (params?.search) {
      const searchTerm = params.search.toLowerCase();
      filteredData = filteredData.filter(enquete => {
        const clientName = typeof enquete.client === 'object' ? enquete.client.nom : enquete.client;
        return (
          enquete.titre.toLowerCase().includes(searchTerm) ||
          enquete.description.toLowerCase().includes(searchTerm) ||
          clientName.toLowerCase().includes(searchTerm) ||
          enquete.tags.some(tag => tag.toLowerCase().includes(searchTerm))
        );
      });
    }

    // Filtrage par statut
    if (params?.statut && params.statut !== 'tous') {
      filteredData = filteredData.filter(enquete => enquete.statut === params.statut);
    }

    // Filtrage par priorité
    if (params?.priorite && params.priorite !== 'tous') {
      filteredData = filteredData.filter(enquete => enquete.priorite === params.priorite);
    }

    // Tri
    if (params?.sortBy) {
      filteredData.sort((a, b) => {
        let aValue: any, bValue: any;
        
        switch (params.sortBy) {
          case 'date':
            aValue = new Date(a.dateModification);
            bValue = new Date(b.dateModification);
            break;
          case 'priorite':
            const prioriteOrder = { haute: 3, moyenne: 2, basse: 1 };
            aValue = prioriteOrder[a.priorite as keyof typeof prioriteOrder];
            bValue = prioriteOrder[b.priorite as keyof typeof prioriteOrder];
            break;
          case 'statut':
            const statutOrder = { en_cours: 3, en_attente: 2, terminee: 1, archivee: 0 };
            aValue = statutOrder[a.statut as keyof typeof statutOrder];
            bValue = statutOrder[b.statut as keyof typeof statutOrder];
            break;
          case 'titre':
            aValue = a.titre.toLowerCase();
            bValue = b.titre.toLowerCase();
            break;
          default:
            return 0;
        }

        if (params.sortOrder === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
    }

    // Pagination
    const page = params?.page || 1;
    const limit = params?.limit || 10;
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedData = filteredData.slice(startIndex, endIndex);

    return {
      data: {
        enquetes: paginatedData,
        total: filteredData.length,
        page,
        limit
      },
      success: true
    };
  }

  // Récupération d'une enquête par ID
  async getEnqueteById(id: string): Promise<ApiResponse<EnqueteResponse>> {
    if (this.useLocalData) {
      return this.getEnqueteByIdLocal(id);
    }

    try {
      return await apiService.get<EnqueteResponse>(`/enquetes/${id}`);
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'enquête:', error);
      return this.getEnqueteByIdLocal(id);
    }
  }

  private async getEnqueteByIdLocal(id: string): Promise<ApiResponse<EnqueteResponse>> {
    const enquete = this.localData.find(e => e.id === id);
    
    if (!enquete) {
      throw new ApiError('Enquête non trouvée', 404);
    }

    return {
      data: { enquete },
      success: true
    };
  }

  // Création d'une nouvelle enquête
  async createEnquete(data: EnqueteFormData): Promise<ApiResponse<EnqueteResponse>> {
    if (this.useLocalData) {
      return this.createEnqueteLocal(data);
    }

    try {
      console.log('🚀 Calling API to create enquête:', data);
      
      // Mapper les données frontend vers le format backend
      const backendData = {
        titre: data.titre,
        description: data.description,
        objectifs: data.objectifs,
        elementsFournis: data.elementsFournis,
        statut: data.statut,
        priorite: data.priorite,
        clientId: data.client, // Assume que data.client contient maintenant l'ID du client
        budget: data.budget,
        assigneA: data.assigne,
        tags: data.tags
      };
      
      console.log('📤 Sending to backend:', backendData);
      const result = await apiService.post<EnqueteResponse>('/enquetes', backendData);
      console.log('✅ API call successful:', result);
      return result;
    } catch (error) {
      console.error('❌ Erreur lors de la création de l\'enquête:', error);
      // Fallback vers les données locales en cas d'erreur API
      return this.createEnqueteLocal(data);
    }
  }

  private async createEnqueteLocal(data: EnqueteFormData): Promise<ApiResponse<EnqueteResponse>> {
    const newEnquete: Enquete = {
      id: `enquete-${Date.now()}`,
      ...data,
      dateCreation: new Date().toISOString().split('T')[0],
      dateModification: new Date().toISOString().split('T')[0],
      notes: [],
      documents: []
    };

    this.localData.push(newEnquete);

    return {
      data: { enquete: newEnquete },
      success: true
    };
  }

  // Mise à jour d'une enquête
  async updateEnquete(id: string, data: Partial<EnqueteFormData>): Promise<ApiResponse<EnqueteResponse>> {
    if (this.useLocalData) {
      return this.updateEnqueteLocal(id, data);
    }

    try {
      console.log('🚀 Updating enquête with data:', data);
      
      // Mapper les données frontend vers le format backend
      const backendData: any = {};
      
      if (data.titre !== undefined) backendData.titre = data.titre;
      if (data.description !== undefined) backendData.description = data.description;
      if (data.objectifs !== undefined) backendData.objectifs = data.objectifs;
      if (data.elementsFournis !== undefined) backendData.elementsFournis = data.elementsFournis;
      if (data.statut !== undefined) backendData.statut = data.statut;
      if (data.priorite !== undefined) backendData.priorite = data.priorite;
      if (data.budget !== undefined) backendData.budget = data.budget;
      if (data.client !== undefined) {
        // Si data.client est un objet, extraire l'ID, sinon utiliser la valeur directement
        backendData.clientId = typeof data.client === 'object' && data.client !== null 
          ? (data.client as any).id || (data.client as any).client_id 
          : data.client;
      }
      if (data.assigne !== undefined) backendData.assigneA = data.assigne; // Mapper assigne vers assigneA
      if (data.tags !== undefined) backendData.tags = data.tags;
      
      console.log('📤 Sending to backend:', backendData);
      const result = await apiService.put<EnqueteResponse>(`/enquetes/${id}`, backendData);
      console.log('✅ Update API call successful:', result);
      return result;
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour de l\'enquête:', error);
      return this.updateEnqueteLocal(id, data);
    }
  }

  private async updateEnqueteLocal(id: string, data: Partial<EnqueteFormData>): Promise<ApiResponse<EnqueteResponse>> {
    const index = this.localData.findIndex(e => e.id === id);
    
    if (index === -1) {
      throw new ApiError('Enquête non trouvée', 404);
    }

    this.localData[index] = {
      ...this.localData[index],
      ...data,
      dateModification: new Date().toISOString().split('T')[0]
    };

    return {
      data: { enquete: this.localData[index] },
      success: true
    };
  }

  // Suppression d'une enquête
  async deleteEnquete(id: string): Promise<ApiResponse<{ message: string }>> {
    if (this.useLocalData) {
      return this.deleteEnqueteLocal(id);
    }

    try {
      return await apiService.delete<{ message: string }>(`/enquetes/${id}`);
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'enquête:', error);
      return this.deleteEnqueteLocal(id);
    }
  }

  private async deleteEnqueteLocal(id: string): Promise<ApiResponse<{ message: string }>> {
    const index = this.localData.findIndex(e => e.id === id);
    
    if (index === -1) {
      throw new ApiError('Enquête non trouvée', 404);
    }

    this.localData.splice(index, 1);

    return {
      data: { message: 'Enquête supprimée avec succès' },
      success: true
    };
  }

  // Statistiques des enquêtes
  async getEnquetesStats(): Promise<ApiResponse<{
    total: number;
    enCours: number;
    terminees: number;
    enAttente: number;
    hautePriorite: number;
  }>> {
    if (this.useLocalData) {
      return this.getEnquetesStatsLocal();
    }

    try {
      return await apiService.get<{
        total: number;
        enCours: number;
        terminees: number;
        enAttente: number;
        hautePriorite: number;
      }>(API_ENDPOINTS.ENQUETES_STATS);
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      return this.getEnquetesStatsLocal();
    }
  }

  private async getEnquetesStatsLocal(): Promise<ApiResponse<{
    total: number;
    enCours: number;
    terminees: number;
    enAttente: number;
    hautePriorite: number;
  }>> {
    const stats = {
      total: this.localData.length,
      enCours: this.localData.filter(e => e.statut === 'en_cours').length,
      terminees: this.localData.filter(e => e.statut === 'terminee').length,
      enAttente: this.localData.filter(e => e.statut === 'en_attente').length,
      hautePriorite: this.localData.filter(e => e.priorite === 'haute').length
    };

    return {
      data: stats,
      success: true
    };
  }
}

export const enqueteService = EnqueteService.getInstance(); 